import { useCallback } from 'react';

import { useAdminData } from '../lib/use-admin-data';
import {
  fetchAdminLive,
  fetchAdminSecurity,
  type AdminFilters,
  type LiveMetrics,
  type SecurityState,
} from '../services/api';
import { BreakdownBars } from './BreakdownBars';
import { Feedback } from './Feedback';
import { StatTile } from './StatTile';

/**
 * Aba SEGURANÇA — o estado da blindagem contra abuso e DDoS.
 *
 * Junta duas leituras: os contadores AO VIVO (memória da API, atualizados a
 * cada poucos segundos, sobrevivem à saturação do banco) e o HISTÓRICO do
 * período (429 gravados na telemetria). Durante um ataque, é a tela que
 * responde a "estão batendo, o escudo está segurando, e de quem vem".
 */

interface AdminSecurityTabProps {
  filters: AdminFilters;
}

const LIVE_REFRESH_MS = 3_000;

export function AdminSecurityTab({ filters }: AdminSecurityTabProps) {
  const liveLoader = useCallback(() => fetchAdminLive(60), []);
  const securityLoader = useCallback(() => fetchAdminSecurity(filters), [filters]);

  const live = useAdminData<LiveMetrics>(liveLoader, LIVE_REFRESH_MS);
  const security = useAdminData<SecurityState>(securityLoader, LIVE_REFRESH_MS);

  const error = live.error ?? security.error;
  if (error) return <Feedback kind="error">{error}</Feedback>;
  if (!security.data) return <p className="page-loading">Carregando…</p>;

  const liveSec = security.data.live;
  const period = security.data.period;
  const sseTotal = live.data?.liveConnections.total ?? liveSec.sseConnections;

  return (
    <div className="admin-tab">
      <div className="stat-grid">
        <StatTile
          hero
          label="Requisições barradas (429)"
          value={liveSec.rateLimitedTotal.toLocaleString('pt-BR')}
          detail="pelo rate limit, desde que a API subiu"
          tone={liveSec.rateLimitedTotal > 0 ? 'warning' : 'good'}
        />
        <StatTile
          label="Conexões SSE recusadas"
          value={liveSec.sseRejectedTotal.toLocaleString('pt-BR')}
          detail="acima do teto por IP ou global"
          tone={liveSec.sseRejectedTotal > 0 ? 'warning' : 'neutral'}
        />
        <StatTile
          label="Conexões SSE abertas"
          value={sseTotal.toLocaleString('pt-BR')}
          detail="overlay + moderador agora"
        />
        <StatTile
          label="IPs rastreados"
          value={liveSec.trackedIps.toLocaleString('pt-BR')}
          detail="endereços que o limitador acompanha"
        />
        <StatTile
          label="Barrados no período"
          value={period.blockedTotal.toLocaleString('pt-BR')}
          detail="429 registrados na telemetria"
        />
      </div>

      <div className="admin-grid">
        <BreakdownBars
          title="Rotas mais barradas"
          description="Onde a trava disparou. A rota de envio costuma liderar sob abuso."
          items={period.byRoute.map((row) => ({ label: row.route, value: row.blocked }))}
          emptyMessage="Nenhuma requisição barrada no período — sem abuso detectado."
        />
        <BreakdownBars
          title="IPs mais barrados (período)"
          description="Quem mais bateu na trava. Concentração num IP é abuso claro."
          items={period.byIp.map((row) => ({
            label: row.ip ?? 'desconhecido',
            value: row.blocked,
            detail: row.location ?? undefined,
          }))}
          emptyMessage="Nenhum IP barrado no período."
        />
      </div>

      {liveSec.topOffenders.length > 0 && (
        <section className="admin-section">
          <h3 className="admin-section__title">Bloqueios ao vivo por IP</h3>
          <p className="admin-section__description">
            Contagem em memória, some quando a API reinicia — é o retrato do que está batendo AGORA,
            útil mesmo com o banco sob pressão.
          </p>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Endereço</th>
                  <th scope="col">Bloqueios</th>
                </tr>
              </thead>
              <tbody>
                {liveSec.topOffenders.map((offender) => (
                  <tr key={offender.ip}>
                    <td>
                      <code>{offender.ip}</code>
                    </td>
                    <td className="data-table__number">
                      {offender.blocks.toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="admin-section">
        <h3 className="admin-section__title">Como ler esta tela</h3>
        <ul className="admin-section__list">
          <li>
            <strong>Barrados subindo, 5xx em zero</strong> (aba Ao vivo): a blindagem está fazendo o
            trabalho — o abuso vira 429 em vez de derrubar a instância.
          </li>
          <li>
            <strong>SSE recusadas &gt; 0:</strong> alguém tentou abrir conexões de overlay/mod além
            do teto. É o vetor mais barato contra esta app, e está contido.
          </li>
          <li>
            <strong>Contra volume de rede</strong> (o link saturando antes do código rodar) a defesa
            é a Cloudflare na borda — ver <code>deploy/RUNBOOK-ddos.md</code>.
          </li>
        </ul>
      </section>
    </div>
  );
}
